import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Edit2, ExternalLink, FileText, Plus, ReceiptText, Search, Trash2, WalletCards } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const expenseCategories = [
  'Gaji Staff',
  'Gaji PT',
  'Gaji Coach',
  'Sewa Tempat',
  'Listrik & Air',
  'Internet',
  'Marketing',
  'Maintenance Alat',
  'Pembelian Stok',
  'Biaya Admin',
  'Operasional',
  'Renovasi',
  'Lampu LED',
  'Baju Notaris',
  'Instalasi Kipas',
  'Manual Transisi Sistem',
  'Server',
  'Lainnya',
];

const paymentMethods = ['Cash', 'Transfer', 'QRIS', 'Debit', 'Credit Card'];

const initialForm = {
  merchant_id: '',
  title: '',
  category: 'Operasional',
  amount: '',
  expense_date: new Date().toISOString().slice(0, 10),
  payment_method: 'Cash',
  notes: '',
  photo_path: '',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const normalizeUploadPath = (path) => String(path || '').split('?')[0].split('#')[0].trim();

const isPdfEvidence = (path) => normalizeUploadPath(path).toLowerCase().endsWith('.pdf');

const getEvidenceFilename = (path) => {
  const normalized = normalizeUploadPath(path);
  if (!normalized) return '';
  const parts = normalized.split('/');
  return parts[parts.length - 1] || '';
};

const getValidationStatus = (expense) =>
  String(expense?.validation_status || 'VALIDATED').trim().toUpperCase() === 'DRAFT' ? 'DRAFT' : 'VALIDATED';

const getValidationBadgeClass = (status) => {
  if (status === 'DRAFT') return 'border-amber-500/25 bg-amber-500/10 text-amber-300';
  return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300';
};

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const currentMerchantId = String(user?.merchant_id || '').trim();
  const currentMerchantName = String(user?.merchant_name || '').trim();
  const [expenses, setExpenses] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [validatingExpenseId, setValidatingExpenseId] = useState('');

  const resolveUploadUrl = (path) => {
    if (!path) return '';
    if (String(path).startsWith('http://') || String(path).startsWith('https://')) return path;
    return `${BACKEND_URL}${path}`;
  };

  const merchantMap = useMemo(
    () =>
      merchants.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [merchants]
  );

  const fetchExpenses = async () => {
    try {
      const [expensesRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/expenses`, { withCredentials: true }),
        isAdmin
          ? axios.get(`${API}/merchants`, { withCredentials: true })
          : Promise.resolve({ data: [] }),
      ]);
      setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
      setMerchants(isAdmin && Array.isArray(merchantsRes.data) ? merchantsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!isModalOpen || !isAdmin || editingExpense) return;
    if (formData.merchant_id || !merchants[0]?.id) return;
    setFormData((prev) => ({ ...prev, merchant_id: merchants[0].id }));
  }, [editingExpense, formData.merchant_id, isAdmin, isModalOpen, merchants]);

  const filteredExpenses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return expenses;
    return expenses.filter((expense) =>
      [
        expense.transaction_number,
        expense.title,
        expense.category,
        expense.merchant_name,
        expense.payment_method,
        expense.notes,
        expense.validation_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [expenses, searchQuery]);

  const validatedExpenses = useMemo(
    () => expenses.filter((expense) => getValidationStatus(expense) === 'VALIDATED'),
    [expenses]
  );
  const draftExpenses = useMemo(
    () => expenses.filter((expense) => getValidationStatus(expense) === 'DRAFT'),
    [expenses]
  );

  const totalExpenses = validatedExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const currentMonthTotal = expenses.reduce((sum, expense) => {
    if (getValidationStatus(expense) !== 'VALIDATED') return sum;
    const date = new Date(expense.expense_date);
    const now = new Date();
    if (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    ) {
      return sum + Number(expense.amount || 0);
    }
    return sum;
  }, 0);

  const openCreate = () => {
    setEditingExpense(null);
    setFormData({
      ...initialForm,
      merchant_id: isAdmin ? merchants[0]?.id || '' : currentMerchantId,
    });
    setIsModalOpen(true);
  };

  const openEdit = (expense) => {
    if (getValidationStatus(expense) === 'VALIDATED') {
      alert('Expense yang sudah divalidasi tidak bisa diedit.');
      return;
    }
    setEditingExpense(expense);
    setFormData({
      merchant_id: expense.merchant_id || (isAdmin ? merchants[0]?.id || '' : currentMerchantId),
      title: expense.title || '',
      category: expense.category || 'Operasional',
      amount: String(expense.amount ?? ''),
      expense_date: String(expense.expense_date || new Date().toISOString().slice(0, 10)).slice(0, 10),
      payment_method: expense.payment_method || 'Cash',
      notes: expense.notes || '',
      photo_path: expense.photo_path || '',
    });
    setIsModalOpen(true);
  };

  const handleExpenseImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const body = new FormData();
    body.append('file', file);

    try {
      setUploadingImage(true);
      const { data } = await axios.post(`${API}/uploads/expense-image`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, photo_path: data?.path || '' }));
    } catch (error) {
      console.error('Failed to upload expense image:', error);
      alert(error.response?.data?.detail || 'Gagal upload bukti pengeluaran.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (expense) => {
    const expenseId = String(expense?.id || '').trim();
    if (!expenseId) return;
    if (getValidationStatus(expense) === 'VALIDATED') {
      alert('Expense yang sudah divalidasi tidak bisa dihapus.');
      return;
    }
    if (!window.confirm('Hapus pengeluaran ini?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`, { withCredentials: true });
      fetchExpenses();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert(error.response?.data?.detail || 'Gagal menghapus pengeluaran.');
    }
  };

  const handleValidate = async (expense) => {
    const expenseId = String(expense?.id || '').trim();
    if (!expenseId) return;
    if (getValidationStatus(expense) === 'VALIDATED') {
      alert('Expense ini sudah divalidasi.');
      return;
    }
    if (!window.confirm(`Validasi expense ${expense.transaction_number || expenseId}? Setelah divalidasi, data tidak bisa diedit atau dihapus.`)) {
      return;
    }
    try {
      setValidatingExpenseId(expenseId);
      await axios.post(`${API}/expenses/${expenseId}/validate`, {}, { withCredentials: true });
      fetchExpenses();
    } catch (error) {
      console.error('Failed to validate expense:', error);
      alert(error.response?.data?.detail || 'Gagal validasi expense.');
    } finally {
      setValidatingExpenseId('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const selectedMerchantId = String(
      isAdmin ? formData.merchant_id : currentMerchantId || formData.merchant_id
    ).trim();
    if (!selectedMerchantId) {
      alert('Pilih merchant terlebih dahulu.');
      return;
    }

    const payload = {
      merchant_id: selectedMerchantId,
      title: formData.title.trim(),
      category: formData.category,
      amount: Number(formData.amount || 0),
      expense_date: formData.expense_date,
      payment_method: formData.payment_method,
      notes: formData.notes.trim(),
      photo_path: formData.photo_path || '',
    };

    try {
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/expenses`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setFormData(initialForm);
      fetchExpenses();
    } catch (error) {
      console.error('Failed to save expense:', error);
      alert(error.response?.data?.detail || 'Gagal menyimpan pengeluaran.');
    }
  };

  const amountPreview = formData.amount ? formatCurrency(formData.amount) : '-';
  const formEvidencePath = String(formData.photo_path || '').trim();
  const formEvidenceIsPdf = isPdfEvidence(formEvidencePath);
  const formEvidenceUrl = resolveUploadUrl(formEvidencePath);

  return (
    <DashboardLayout>
      <Header title="Expenses" />
      <div className="p-8" data-testid="expenses-page">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              Finance
            </Badge>
            <h2 className="text-2xl font-semibold text-white">Pengeluaran</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Expense baru disimpan sebagai draft, lalu validasi untuk mengunci data dan masuk ke laporan Profit Loss.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="rounded-sm bg-[#D4AF37] text-black hover:bg-[#C59A27]"
            data-testid="add-expense-btn"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} />
            Tambah Pengeluaran
          </Button>
        </div>

        <div className="mb-5 grid gap-4 md:grid-cols-3">
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Pengeluaran (Validated)</p>
            <p className="mt-3 text-2xl font-semibold text-white">{formatCurrency(totalExpenses)}</p>
          </div>
          <div className="rounded-sm border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.12),rgba(9,9,11,0.84))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Bulan Ini (Validated)</p>
            <p className="mt-3 text-2xl font-semibold text-[#D4AF37]">{formatCurrency(currentMonthTotal)}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Jumlah Draft</p>
            <p className="mt-3 text-2xl font-semibold text-white">{draftExpenses.length}</p>
          </div>
        </div>

        <div className="mb-5 border border-white/10 bg-zinc-950/35 p-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" strokeWidth={1.6} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari pengeluaran, merchant, kategori, status, metode bayar..."
              className="h-11 rounded-sm border-white/10 bg-black/20 pl-11 text-white placeholder:text-zinc-600 focus:border-[#D4AF37]"
              data-testid="expenses-search"
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10 bg-zinc-950/40">
          <div className="hidden grid-cols-[.85fr_1.2fr_.9fr_.8fr_.7fr_.8fr_.8fr_1fr] gap-5 border-b border-white/10 bg-white/[0.025] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 lg:grid">
            <span>No. Transaksi</span>
            <span>Pengeluaran</span>
            <span>Merchant</span>
            <span>Kategori</span>
            <span>Status</span>
            <span>Tanggal</span>
            <span>Nominal</span>
            <span className="text-right">Aksi</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : filteredExpenses.length > 0 ? (
            <div className="divide-y divide-white/5">
              {filteredExpenses.map((expense) => {
                const validationStatus = getValidationStatus(expense);
                const isValidated = validationStatus === 'VALIDATED';
                const isValidating = validatingExpenseId === expense.id;
                const evidencePath = String(expense.photo_path || '').trim();
                const evidenceIsPdf = isPdfEvidence(evidencePath);
                const evidenceUrl = resolveUploadUrl(evidencePath);

                return (
                  <div key={expense.id} className="grid gap-4 px-5 py-4 transition-colors hover:bg-white/[0.03] lg:grid-cols-[.85fr_1.2fr_.9fr_.8fr_.7fr_.8fr_.8fr_1fr] lg:gap-5">
                    <p className="text-xs font-semibold text-zinc-300">{expense.transaction_number || '-'}</p>
                    <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/10 text-[#D4AF37]">
                        {evidencePath ? (
                          evidenceIsPdf ? (
                            <FileText className="h-4 w-4" strokeWidth={1.7} />
                          ) : (
                            <img
                              src={evidenceUrl}
                              alt={expense.title}
                              className="h-full w-full rounded-sm object-cover"
                            />
                          )
                        ) : (
                          <ReceiptText className="h-4 w-4" strokeWidth={1.7} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{expense.title}</p>
                        <p className="mt-1 truncate text-xs text-zinc-500">{expense.notes || expense.payment_method}</p>
                        {evidencePath ? (
                          <a
                            href={evidenceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200"
                          >
                            Lihat bukti {evidenceIsPdf ? '(PDF)' : '(Gambar)'}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <p className="truncate text-sm text-zinc-300">
                      {expense.merchant_name || merchantMap[String(expense.merchant_id || '')]?.name || '-'}
                    </p>
                    <div>
                      <span className="rounded-sm border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-zinc-300">
                        {expense.category}
                      </span>
                    </div>
                    <div>
                      <span className={`rounded-sm border px-2.5 py-1 text-xs font-semibold ${getValidationBadgeClass(validationStatus)}`}>
                        {validationStatus === 'DRAFT' ? 'Draft' : 'Validated'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">{formatDate(expense.expense_date)}</p>
                    <p className="text-sm font-semibold text-red-300">{formatCurrency(expense.amount)}</p>
                    <div className="flex items-center justify-end gap-2">
                      {isValidated ? (
                        <span className="rounded-sm border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-300">
                          Tervalidasi
                        </span>
                      ) : (
                        <button
                          onClick={() => handleValidate(expense)}
                          disabled={isValidating}
                          className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:cursor-wait disabled:opacity-60"
                          data-testid={`validate-expense-${expense.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {isValidating ? 'Validasi...' : 'Validasi'}
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(expense)}
                        disabled={isValidated || isValidating}
                        className="rounded-sm p-2 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid={`edit-expense-${expense.id}`}
                      >
                        <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleDelete(expense)}
                        disabled={isValidated || isValidating}
                        className="rounded-sm p-2 text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid={`delete-expense-${expense.id}`}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <WalletCards className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
              <p className="text-sm font-medium text-white">Belum ada pengeluaran</p>
              <p className="mt-1 text-sm text-zinc-500">Tambahkan biaya operasional pertama untuk laporan Profit Loss.</p>
            </div>
          )}
        </div>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-sm border-white/10 bg-zinc-900 text-white">
            <DialogHeader className="border-b border-white/10 pb-4">
              <DialogTitle>{editingExpense ? 'Edit Draft Pengeluaran' : 'Tambah Pengeluaran (Draft)'}</DialogTitle>
              <p className="mt-1 text-sm text-zinc-500">
                Isi data utama terlebih dahulu, lalu upload bukti pembayaran (gambar/PDF) bila tersedia.
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-5 space-y-5">
              <div className="space-y-4 rounded-sm border border-white/10 bg-zinc-950/45 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4AF37]">Informasi Pengeluaran</p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Pengeluaran</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="rounded-sm border-white/10 bg-zinc-950 text-white"
                      placeholder="Contoh: Gaji trainer"
                      required
                      data-testid="expense-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Merchant</Label>
                    {isAdmin ? (
                      <select
                        value={formData.merchant_id}
                        onChange={(e) => setFormData({ ...formData, merchant_id: e.target.value })}
                        className="h-10 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
                        required
                        data-testid="expense-merchant-select"
                      >
                        <option value="">Pilih merchant</option>
                        {merchants.map((merchant) => (
                          <option key={merchant.id} value={merchant.id}>
                            {merchant.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={currentMerchantName || '-'}
                        disabled
                        className="rounded-sm border-white/10 bg-zinc-950 text-white/80"
                        data-testid="expense-merchant-readonly"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Kategori</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="h-10 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
                      data-testid="expense-category-select"
                    >
                      {expenseCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nominal</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="rounded-sm border-white/10 bg-zinc-950 text-white"
                      required
                      data-testid="expense-amount-input"
                    />
                    <p className="text-xs text-zinc-500">Preview: {amountPreview}</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tanggal</Label>
                    <Input
                      type="date"
                      value={formData.expense_date}
                      onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                      className="rounded-sm border-white/10 bg-zinc-950 text-white"
                      required
                      data-testid="expense-date-input"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Metode Bayar</Label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      className="h-10 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
                      data-testid="expense-payment-method-select"
                    >
                      {paymentMethods.map((method) => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-sm border border-white/10 bg-zinc-950/45 p-4">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Catatan (Opsional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="min-h-24 rounded-sm border-white/10 bg-zinc-950 text-white"
                  placeholder="Misal: pembayaran vendor bulan Mei."
                  data-testid="expense-notes-input"
                />
              </div>

              <div className="space-y-3 rounded-sm border border-white/10 bg-zinc-950/45 p-4">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Bukti Pembayaran (Opsional)</Label>
                <Input
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  onChange={handleExpenseImageUpload}
                  className="rounded-sm border-white/10 bg-zinc-950 text-white file:mr-3 file:rounded-sm file:border-0 file:bg-[#D4AF37] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-black"
                />
                <p className="text-xs text-zinc-500">Format: JPG, PNG, WEBP, GIF, atau PDF.</p>
                {uploadingImage && <p className="text-xs text-zinc-400">Mengupload bukti...</p>}

                {formEvidencePath ? (
                  <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-3">
                    {formEvidenceIsPdf ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
                          <p className="truncate text-sm text-zinc-200">{getEvidenceFilename(formEvidencePath) || 'Bukti PDF'}</p>
                        </div>
                        <a
                          href={formEvidenceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 text-xs text-sky-300 hover:text-sky-200"
                        >
                          Buka PDF
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <img
                          src={formEvidenceUrl}
                          alt="Bukti pengeluaran"
                          className="max-h-48 w-auto rounded-sm object-cover"
                        />
                        <a
                          href={formEvidenceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200"
                        >
                          Lihat gambar penuh
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, photo_path: '' }))}
                      className="mt-3 text-xs text-red-400 hover:text-red-300"
                    >
                      Hapus bukti
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">Belum ada bukti diupload.</p>
                )}
              </div>

              <div className="grid gap-3 pt-2 md:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="rounded-sm border-white/10 text-white hover:bg-zinc-800"
                >
                  Cancel
                </Button>
                <Button type="submit" className="rounded-sm bg-[#D4AF37] text-black hover:bg-[#C59A27]" data-testid="expense-submit-btn">
                  {editingExpense ? 'Update Draft' : 'Simpan Draft'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

